import { Component, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { NotificationService } from '@progress/kendo-angular-notification';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { ServiceAccount } from '../../../../../../../core/models/entities';
import { environment } from '../../../../../../../../environments/environment';
import { ServiceAccountsForm, AccountsFormService } from './service/account-form.service';
import { ServiceAccountRequest, SoarServiceAccountService } from './service/soar-service-account.service';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-accounts',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DialogModule,
    DropDownsModule,
    ReactiveFormsModule,
    DatatableComponent,
  ],
  templateUrl: './accounts.component.html',
  styleUrl: './accounts.component.scss',
})
export class AccountsComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(SoarServiceAccountService);
  public route = inject(ActivatedRoute);
  private formService = inject(AccountsFormService);
  private notificationService = inject(NotificationService);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/serviceaccounts`;
  public id!: number;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<ServiceAccountsForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  private rows = new Map<string, ServiceAccount>();

  public sensitivityOptions = ['Critical', 'High', 'Medium', 'Low'];


  public sensitivityFilterOptions = this.sensitivityOptions.map((value) => ({ text: value, value }));

  private sensitivityFilter: string | null = null;

  public initialFilters = this.buildFilters();

  public datatableColumns = [
    { data: 'username', name: 'username', title: 'Username', type: 'string', render: DataTable.render.text() },
    {
      data: 'related_function',
      name: 'related_function',
      title: 'Related Function',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: 'sensitivity',
      name: 'sensitivity',
      title: 'Sensitivity/Function',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: ServiceAccount) => {
        this.rows.set(String(row.pk), row);
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm action-edit" data-pk="${row.pk}">
              Edit
            </button>
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm action-remove" data-pk="${row.pk}">
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          // Still the source of truth for update and delete, which build their
          // endpoint from it, even though the grid no longer reads through the
          // service.
          this.gridService.url = `${this.baseUrl}(${this.id})/`;
          this.rows.clear();
          this.datatableUrl.set(this.gridService.url);
        });
      },
      { allowSignalWrites: true }
    );
  }

  private buildFilters(): Record<string, string> {
    const filters: Record<string, string> = {};
    if (this.sensitivityFilter != null) {
      filters['$filter'] = `sensitivity eq '${this.sensitivityFilter}'`;
    }
    return filters;
  }

  public sensitivityChange($event: { text: string; value: string | null }): void {
    this.sensitivityFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    const pk = button?.getAttribute('data-pk');
    if (!button || !pk) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(this.rows.get(pk));
    } else if (button.classList.contains('action-remove')) {
      this.remove(pk);
    }
  }

  public openEditor(account?: ServiceAccount): void {
    this.isNew.set(!account);
    this.formGroup.set(this.formService.createAccountsForm(account));
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || this.isSaving()) {
      return;
    }
    const formValue = formGroup.value;
    const payload: ServiceAccountRequest = {
      organization_id: this.id,
      service_account: {
        pk: this.isNew() ? null : formValue?.pk!,
        username: formValue?.username!,
        related_function: formValue?.related_function!,
        sensitivity: formValue?.sensitivity!,
      },
    };

    const request = this.isNew()
      ? this.gridService.createSoarServiceAccount(payload)
      : this.gridService.updateSoarServiceAccount(payload.service_account);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.showError('Failed to save the service account.');
      },
    });
  }

  private remove(pk: string): void {
    this.gridService.deleteSoarServiceAccount(Number(pk)).subscribe({
      next: () => {
        this.table?.reload();
      },
      error: (error) => {
        console.error(error);
        this.showError('Failed to delete the service account.');
      },
    });
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-error',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: true,
      hideAfter: 5000,
    });
  }
}
